import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useLang } from '@/i18n'
import { siteConfig } from '@/constants/site'

/**
 * Page title, description and social tags. Pass only the page name as `title`;
 * the localized site name (siteConfig.localizedName) is appended for you.
 */
export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage = siteConfig.ogImage,
    noIndex = false
}): ReactNode => {
    const { lang } = useLang()
    const siteName = siteConfig.localizedName[lang]
    const pageTitle = title ? `${title} - ${siteName}` : siteName
    const pageDescription = description ?? siteConfig.localizedDescription[lang]

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={pageDescription} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={pageDescription} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteName} />
            <meta property='og:locale' content={lang === 'ar' ? 'ar_EG' : 'en_US'} />
            {siteConfig.url && <meta property='og:url' content={siteConfig.url} />}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={pageDescription} />

            {ogImage && <meta property='og:image' content={ogImage} />}
            {ogImage && <meta name='twitter:image' content={ogImage} />}
            {noIndex && <meta name='robots' content='noindex' />}
        </Head>
    )
}
